import React from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import WorkersByGroupChart from 'components/charts/WorkersByGroupChart';
import WorkersInBlockChart from 'components/charts/WorkersInBlockChart';
import SectionIntro from 'components/common/SectionIntro';
import TableComponent from 'components/common/TableComponent';
import { useWorkdaySummary } from 'hooks/useWorkdaySummary';
import { parseDate } from 'utils';

const FarmActivitySummaryTable = ({ farmActivityData }) => {
  const { t } = useTranslation();
  const tableData = {
    columns: [
      { key: 'activityName', label: t('table.columns.activityName') },
      { key: 'blockName', label: t('table.columns.blockName') },
      { key: 'groupName', label: t('table.columns.groupName') },
      { key: 'cropName', label: t('table.columns.crop') },
      { key: 'varietyName', label: t('table.columns.variety') },
      { key: 'numberOfPlants', label: t('dashboard.total') },
      { key: 'workerCount', label: t('dashboard.workersNumber') },
      { key: 'firstScan', label: t('table.columns.firstScan'), cell: value => parseDate(value) },
      { key: 'lastScan', label: t('table.columns.lastScan'), cell: value => parseDate(value) }
    ],
    rows: farmActivityData
  };

  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />;
};

FarmActivitySummaryTable.propTypes = {
  farmActivityData: PropTypes.arrayOf(PropTypes.object)
};

const LastActivitiesBoardTable = ({ lastActivitiesData }) => {
  const { t } = useTranslation();
  const tableData = {
    columns: [
      { key: 'workerId', label: t('table.columns.workerId') },
      { key: 'workerName', label: t('table.columns.workerName') },
      { key: 'lastActivity', label: t('dashboard.lastActivity') },
      { key: 'timePassed', label: t('dashboard.timePassed') },
      { key: 'blockName', label: t('table.columns.blockName') },
      {
        key: 'location',
        label: t('dashboard.location'),
        cell: value => (value ? value.split(',').slice(0, 2).join(', ') : '')
      }
    ],
    rows: lastActivitiesData
  };

  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />;
};

LastActivitiesBoardTable.propTypes = {
  lastActivitiesData: PropTypes.arrayOf(PropTypes.object)
};

const formatDate = dateString => {
  const parsedDate = DateTime.fromISO(dateString);
  return parsedDate.isValid ? parsedDate.toFormat('dd.MM.yyyy') : dateString;
};

const LastDayWorkingSummary = () => {
  const { t } = useTranslation();
  const { data, updateFilterDate } = useWorkdaySummary();

  const handleDateChange = e => {
    updateFilterDate(e.target.value);
  };

  const displayDate = formatDate(data.filterDate);

  return (
    <div className="workspace-workday-summary-page min-h-screen p-6 sm:px-4">
      <SectionIntro
        kicker={t('pageIntro.dashboardKicker')}
        title={t('dashboard.lastWorkingDaySummary')}
        description={t('pageIntro.lastWorkingDaySummaryDescription')}
        actions={
          <div className="flex items-center gap-2">
            <label htmlFor="datePicker" className="workspace-summary-date-label">
              {t('dashboard.selectDate')}
            </label>
            <input
              id="datePicker"
              type="date"
              value={data.filterDate}
              onChange={handleDateChange}
              className="workspace-summary-date-input rounded border px-2 py-1"
              max={data.currentDate}
            />
          </div>
        }
        className="mb-4"
      />

      <hr className="workspace-summary-divider my-5 border-t" />

      {data.error && (
        <div className="workspace-summary-error-banner mb-6 rounded border px-4 py-3">
          {data.error}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="workspace-report-surface rounded-md border p-4">
          <h2 className="workspace-report-section-title mb-4 font-command-display text-2xl">
            {t('dashboard.workersByGroup')}
          </h2>
          <p className="workspace-report-section-meta">
            {t('dashboard.lastWorkingDay')} ({displayDate})
          </p>
          {data.loading ? (
            <div className="flex h-96 min-h-[600px] items-center justify-center p-6">
              <div className="workspace-summary-message">{t('dashboard.loadingChartData')}</div>
            </div>
          ) : (
            <WorkersByGroupChart data={data.workersByGroup} totalWorkers={data.totalWorkers} />
          )}
        </section>

        <section className="workspace-report-surface rounded-md border p-4">
          <h2 className="workspace-report-section-title mb-4 font-command-display text-2xl">
            {t('dashboard.workersInBlock')}
          </h2>
          <p className="workspace-report-section-meta">
            {t('dashboard.lastWorkingDay')} ({displayDate})
          </p>
          {data.loading ? (
            <div className="flex h-96 min-h-[600px] items-center justify-center p-6">
              <div className="workspace-summary-message">{t('dashboard.loadingChartData')}</div>
            </div>
          ) : (
            <WorkersInBlockChart data={data.workersInBlock} />
          )}
        </section>
      </div>

      <section className="workspace-report-surface mt-12 rounded-md border p-4">
        <h2 className="workspace-report-section-title mb-4 font-command-display text-2xl">
          {t('dashboard.farmActivitySummary')}
        </h2>
        {data.loading ? (
          <div className="workspace-summary-message">{t('dashboard.loadingFarmActivityData')}</div>
        ) : (
          <FarmActivitySummaryTable farmActivityData={data.farmActivityData} />
        )}
      </section>

      <section className="workspace-report-surface mt-12 rounded-md border p-4">
        <h2 className="workspace-report-section-title mb-4 font-command-display text-2xl">
          {t('dashboard.lastActivitiesBoard')}
        </h2>
        {data.loading ? (
          <div className="workspace-summary-message">
            {t('dashboard.loadingLastActivitiesData')}
          </div>
        ) : (
          <LastActivitiesBoardTable lastActivitiesData={data.lastActivitiesData} />
        )}
      </section>
    </div>
  );
};

export default LastDayWorkingSummary;
